import { useCallback } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import type { TaskSubjectWrite } from "@storyboard/web-client";
import { recordTaskPinBoards, recordTasks, taskPinBoardWriteFor, taskWriteFor } from "@/models/task/taskWrites";
import type { DispatchTableRow } from "./dispatchTableRow";
import { openTaskOfTarget, type TaskAbout, type TaskFold, taskGrainFor, taskWritesFor } from "./tasks";

/** What a new task carries beyond its subject: the board, the note, and the expiry, each null for none. */
export type NewTaskIntent = { viewId: string | null; note: string | null; expiresAt: string | null };

/** Who makes the tasks, and where: what every task write on the page is stamped with. */
export type TaskScope = { workspaceId: string; membershipId: string | undefined };

/**
 * The task writes the page makes: new tasks from rows or a History entry, and a task moved to another board.
 *
 * A bulk selection writes one batch. A message or event that already carries an open task gets that task moved to the
 * chosen board instead of a second task; a driver can carry many. A task made from rows is about each row's message
 * or event, or its driver, as the dispatcher chose; one from a History entry names the subject it is handed, whatever
 * the view's grain. A task closes by being acknowledged, which the page's acknowledgement writer does, or by its
 * expiry passing. A task moved instead of made keeps its own expiry: the note and the expiry go only on a new task.
 *
 * @param grain - The view's grain, which decides what a row's task is about.
 * @param scope - Who makes the tasks, and where.
 * @param fold - The workspace's open tasks, to find a row's task already in force.
 */
export function useTasks(
  grain: DispatchGrain,
  scope: TaskScope,
  fold: TaskFold,
): {
  makeTasks: (rows: readonly DispatchTableRow[], about: TaskAbout, intent: NewTaskIntent) => void;
  makeTaskFor: (subject: TaskSubjectWrite, intent: NewTaskIntent) => void;
  moveTask: (task: OpenTask, viewId: string) => void;
} {
  const { workspaceId, membershipId } = scope;
  const writer = useCallback(
    () => ({ workspaceId, actorWorkspaceMembershipId: membershipId ?? null }),
    [workspaceId, membershipId],
  );

  const makeTasks = useCallback(
    (rows: readonly DispatchTableRow[], about: TaskAbout, intent: NewTaskIntent) => {
      const { viewId } = intent;
      const { subjects, moves } = taskWritesFor(rows, taskGrainFor(grain, about), fold);
      recordTasks(
        writer(),
        subjects.map((subject) => taskWriteFor({ subjects: [subject], ...intent })),
      );
      if (viewId !== null) {
        recordTaskPinBoards(
          writer(),
          moves.map(({ task }) => taskPinBoardWriteFor({ taskId: task.id, viewId })),
        );
      }
    },
    [grain, fold, writer],
  );

  const makeTaskFor = useCallback(
    (subject: TaskSubjectWrite, intent: NewTaskIntent) => {
      const { viewId } = intent;
      // A message or event that already carries an open task has that task moved; a driver gets another.
      const current = subject.subject === "membership" ? null : openTaskOfTarget(subject, fold);
      if (current === null) {
        recordTasks(writer(), [taskWriteFor({ subjects: [subject], ...intent })]);
        return;
      }
      if (viewId !== null) {
        recordTaskPinBoards(writer(), [taskPinBoardWriteFor({ taskId: current.task.id, viewId })]);
      }
    },
    [fold, writer],
  );

  const moveTask = useCallback(
    (task: OpenTask, viewId: string) =>
      recordTaskPinBoards(writer(), [taskPinBoardWriteFor({ taskId: task.task.id, viewId })]),
    [writer],
  );

  return { makeTasks, makeTaskFor, moveTask };
}
